import {
  AdvanceQualificationMethod,
  CustomerApplicationStatusEnum,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { isQualified } from 'features/advance/helpers/AdvanceProducts.helpers';
import useReviewScreenOnRenderQueries from 'features/advance/hooks/review/useReviewScreenOnRenderQueries';
import useVaroAdvanceProducts from 'features/advance/hooks/useGetVaroAdvanceProducts';
import useSubmitApplication from 'features/advance/hooks/useSubmitApplication';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

import ReviewContent from './ReviewContent';

const ReviewScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'review',
  });
  const { bottom } = useSafeAreaInsets();

  const {
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    autoPaymentAgreementId,
    qualificationMethod,
  } = useLocalSearchParams<{
    dueDate: string;
    feeAmount: string;
    amountId: string;
    advanceAmount: string;
    repaymentDate: string;
    selectedAccountId?: string;
    paymentSource: LendingPaymentSource;
    selectedAccountName: string;
    autoPaymentAgreementId?: string;
    qualificationMethod?: AdvanceQualificationMethod;
  }>();

  const amountWithFee = parseFloat(advanceAmount) + parseFloat(feeAmount);
  const totalRepaymentAmountWithFee = amountWithFee.toString();

  const {
    data: {
      backupPaymentMethodsResponse,
      reviewListItems,
      dynamicAgreements,
      agreementIds,
    },
    isFetching: isFetchingInitialQueries,
    hasError: initialQueriesHasError,
  } = useReviewScreenOnRenderQueries({
    dueDate,
    feeAmount,
    advanceAmount,
    repaymentDate,
    selectedAccountName,
    totalRepaymentAmountWithFee,
    paymentSource,
    selectedAccountId,
  });
  const { data: advanceProductsData } = useVaroAdvanceProducts('cache-first');
  const feeTier = isQualified(advanceProductsData)
    ? advanceProductsData.options.find((option) => option.id === amountId)
        ?.feeTier
    : undefined;

  const {
    error: submitAdvanceError,
    showLoadingScreen,
    advanceStatus,
    executeMutation: submitApplicationMutation,
  } = useSubmitApplication();

  const hasUrlParams =
    !!dueDate &&
    !!feeAmount &&
    !!amountId &&
    !!advanceAmount &&
    !!repaymentDate &&
    !!paymentSource &&
    !!qualificationMethod;

  const isLoading = showLoadingScreen || isFetchingInitialQueries;

  useEffect(() => {
    switch (advanceStatus) {
      case CustomerApplicationStatusEnum.Approved:
        router.dismissAll();
        router.navigate({
          pathname: '/varo-advance/success',
          params: {
            advanceAmount,
            feeTier,
            formattedAdvanceAmount: formatCurrency(advanceAmount, true),
            message: translatedText('message', {
              advanceAmount: '$' + advanceAmount,
            }),
          },
        });
        break;
      case CustomerApplicationStatusEnum.Incomplete:
      case CustomerApplicationStatusEnum.ReadyToSubmit:
      case CustomerApplicationStatusEnum.Submitted:
      case CustomerApplicationStatusEnum.Unknown:
      case CustomerApplicationStatusEnum.InReview:
      case CustomerApplicationStatusEnum.Declined:
        // The first 5 statuses should never be returned for advance
        // but technically are possible
        router.dismissAll();
        router.navigate({
          pathname: '/varo-advance/error',
          params: {
            subcategory: 'va_application',
            error_type: 'unknown',
            error_description: submitAdvanceError?.messageBody,
            step_name: 'va_application_submission',
          },
        });
        break;
      case CustomerApplicationStatusEnum.Pending:
        router.dismissAll();
        router.navigate('/varo-advance/processing');
        break;
    }
  }, [
    advanceAmount,
    advanceStatus,
    feeTier,
    submitAdvanceError?.messageBody,
    translatedText,
    router,
  ]);

  const handleGetAdvancePress = () => {
    if (agreementIds?.length) {
      const stringArrayToNumberArray = autoPaymentAgreementId?.split(',');
      const ids = [
        ...agreementIds,
        ...(stringArrayToNumberArray ? stringArrayToNumberArray : []),
      ].filter((id): id is string => Boolean(id));
      // This is needed because in ios, the screen will reappear briefly between
      // the time fetching is false and router.push happens
      void submitApplicationMutation(
        amountId,
        repaymentDate,
        ids,
        selectedAccountId,
        paymentSource,
      );
    }
  };

  const isReviewListEmpty = !reviewListItems?.items?.length;

  const hasError =
    submitAdvanceError || initialQueriesHasError || isReviewListEmpty;

  // !isLoading is needed otherwise we crash the app with error stating
  if (!hasUrlParams && !isLoading) {
    // if this is the true, it's likely the user came from a bookmarked link
    // and customer has not selected an amount yet, or we lost local state.
    // we need to figure out their status and send them to the right place. (likely select amount screen)
    return <Redirect href="/varo-advance" />;
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: !hasError && !isLoading,
          headerBackVisible: !hasError && !isLoading,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isLoading && !hasError}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: () => router.dismissTo('/'),
              }}
            />
          ) : null
        }
      />

      {reviewListItems && backupPaymentMethodsResponse && hasUrlParams ? (
        <ReviewContent
          reviewListItems={reviewListItems.items}
          formattedAdvanceAmount={formatCurrency(advanceAmount, true)}
          qualificationMethod={qualificationMethod}
          paymentSource={paymentSource}
          submitApplication={handleGetAdvancePress}
          dynamicAgreements={dynamicAgreements}
          dynamicAgreementsForDisbursements={dynamicAgreements}
          backupPaymentMethods={backupPaymentMethodsResponse}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingBottom: theme.spacing.m,
  },
});

export default ReviewScreen;
